import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';

import { Tabs, Tab, Grid, Divider, Card } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import DashboardLayout from '../DashboardLayout';
import PageHeader from '../PageHeader';
import { Loader } from '../Loading';
import SubscriptionUsers from '../../containers/SubscriptionUsers';
import SubscriptionInvitations from '../../containers/SubscriptionInvitations';
import SubscriptionDomains from '../../containers/SubscriptionDomains';
import ExistingLicenceBanner from '../../containers/ExistingLicenceBanner';
import SubscriptionCard from './SubscriptionCard';
import ResourcesCard from '../resources/ResourcesCard';
import GenericNotFound from '../GenericNotFound';
import Button from '../Button';
import { withNavigation } from '../../actions/navigation';
import SetCurrentProduct from '../../containers/SetCurrentProduct';
import UserInvitations from '../../containers/UserInvitations';
import AlertBanner from '../../containers/AlertBanner';
import WarningBanner from './WarningBanner';
import WarningIcon from '@material-ui/icons/Warning';

const INVITATIONS_TAB = "invitations";
const USERS_TAB = "users";
const DOMAINS_TAB = "domains";

/**
 * Subscription Page component, containing
 * Overview of the subscription (Subscription Card) with "change subscription" form
 * Global resource usage card
 * Tabs with subscription invitations and subscription users
 */

const styles = theme => ({
  defaultText: {
    color: '#797979',
    fontWeight: 300
  },
  button: {
    marginTop: 14,
    marginBottom: 14,
  },
  card: {
    marginTop: 24,
    marginBottom: 24,
    borderRadius: 12,
  },
  label: {
    textTransform: 'none',
    fontSize: 16
  },
  tabIcon: {
    marginLeft: 8,
    color: 'red'
  }
});

class SubscriptionPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentTab: USERS_TAB
    };
  }

  /**
   * Called when a tab is clicked
   * @param {Event} e 
   * @param {String} currentTab 
   */
  handleTab(e, currentTab) {
    this.setState({ currentTab });
  }

  render() {
    const {
      intl,
      user,
      subscription,
      product,
      account,
      navigateBackToProduct,
      editSubscription,
      classes,
      dismissSubscriptionWarning
    } = this.props;

    const {
      currentTab
    } = this.state;

    if (!subscription) {
      return <DashboardLayout
        title={
          intl.messages['subscription.page.title']
        }>
        <Loader />
      </DashboardLayout>;
    }

    if (subscription.notFound) {
      return (
        <DashboardLayout>
          <GenericNotFound
            message={
              <FormattedMessage id='subscription.notFound' />
            }
          />
        </DashboardLayout>
      );
    }

    const domainTabHasWarning = subscription.warnings && subscription.warnings.filter(warning => warning.scope === "DOMAIN").length > 0;

    const isDomainTabDisplayed = product && product.plans && !!product.plans.find(plan => !!plan.domainInvitationSettings);

    return (
      <DashboardLayout
        banner={
          <React.Fragment>
            { 
              user.alerts 
              && user.alerts.map(alert => <AlertBanner key={alert.id} alert={alert} user={user} />)
            }
            {
              subscription.warnings
              && subscription.warnings.map(warning => <WarningBanner key={warning.id} subscriptionId={subscription.id} alert={warning} user={user} dismiss={() => { dismissSubscriptionWarning(subscription.id, warning.id); } } />)
            }
            <UserInvitations productId={product && product.id} />
            {
              product && product.id && account && account.id
              && <ExistingLicenceBanner 
                    productId={product.id} 
                    userId={account.id} 
                  />
            }
          </React.Fragment>
        }
        title={
          product
            ? intl.formatMessage(
              {
                id: 'subscription.page.titleWithProduct'
              },
              {
                productName: product.name
              }
            )
            : intl.messages['subscription.page.title']
        }>
        {
          product && <SetCurrentProduct product={product} />
        }
        {subscription && subscription.plan // FIXME: not a good way to know whether it's loaded
          ? (
            <div>
              <PageHeader
                actions={
                  <div>
                    {
                      product
                      && <Button
                        size="medium"
                        onClick={_ => navigateBackToProduct(product)}
                      >
                        <FormattedMessage
                          id="product.button.backToProduct"
                          values={{
                            productName: product.name
                          }}
                        />
                      </Button>
                    }
                  </div>
                }
              >

                <Typography variant="h4" className={classes.defaultText} >
                  {
                    product
                      ? <FormattedMessage id='subscription.page.titleWithProduct' values={{ productName: product.name }} />
                      : <FormattedMessage id='subscription.page.title' />
                  }
                </Typography>
              </PageHeader>

              <Grid container spacing={24} direction='row' >
                <Grid item md={4} sm={12}>

                  <SubscriptionCard
                    subscription={subscription}
                    actions={ 
                      !subscription.locked 
                      && (product
                        ? (
                          <div style={{ textAlign: 'center' }}>
                            <Divider />
                            <Button
                              size='medium'
                              onClick={editSubscription}
                              variant='text'
                              className={classes.button}>
                              <FormattedMessage id='subscription.change.button.updateSubscription' />
                            </Button>
                          </div>
                        )
                        : (
                          <Loader />
                        )
                      )
                    }
                  />
                </Grid>
                <Grid item md={8} sm={12}>

                  <ResourcesCard subscription={subscription} />

                </Grid>
              </Grid>
              <Card className={classes.card} >
                <Tabs
                  indicatorColor="primary"
                  value={currentTab}
                  onChange={(e, value) => this.handleTab(e, value)}
                >
                  <Tab
                    className={classes.label}
                    value={USERS_TAB}
                    label={
                      <FormattedMessage id='subscription.users.tab.label' />
                    }
                  />
                  <Tab
                    className={classes.label}
                    value={INVITATIONS_TAB}
                    label={
                      <FormattedMessage id='subscription.invitations.tab.label' />
                    }
                  />
                  {isDomainTabDisplayed &&
                    <Tab
                      className={classes.label}
                      value={DOMAINS_TAB}
                      label={<div>
                          <FormattedMessage id='subscription.domains.tab.label' />
                          { domainTabHasWarning && <WarningIcon fontSize="small" className={classes.tabIcon} /> }
                        </div>
                      }
                    />
                  }
                </Tabs>
                {currentTab === INVITATIONS_TAB && (
                  <SubscriptionInvitations
                    subscription={subscription}
                  />
                )}
                {currentTab === USERS_TAB && (
                  <SubscriptionUsers
                    subscription={subscription}
                  />
                )}
                {isDomainTabDisplayed && currentTab === DOMAINS_TAB && (
                  <SubscriptionDomains
                    subscription={subscription}
                    user={user}
                  />
                )}
              </Card>
            </div>
          )
          : (
            <Loader />
          )}
      </DashboardLayout>
    )
  }

  componentWillMount() {
    const { loadSubscription } = this.props;
    loadSubscription();
  }

  componentWillReceiveProps({ subscriptionId, loadSubscription }) {
    if (subscriptionId !== this.props.subscriptionId) {
      loadSubscription();
      this.setState({currentTab: USERS_TAB});
    }
  }
}

SubscriptionPage.propTypes = {
  intl: intlShape,
  loadSubscription: PropTypes.func.isRequired,
  subscriptionId: PropTypes.string.isRequired,
  subscription: PropTypes.object,
  product: PropTypes.object,
  account: PropTypes.object,
  handlePaymentInfo: PropTypes.func.isRequired,
  editSubscription: PropTypes.func.isRequired,
  navigateBackToProduct: PropTypes.func.isRequired,
}

export default injectIntl(
  withStyles(styles)(
    withNavigation(SubscriptionPage)
  )
);


// WEBPACK FOOTER //
// ./src/components/subscriptions/SubscriptionPage.js